import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Info, Moon, Sun, FileText, Code, Eye, EyeOff } from "lucide-react";
import { jsPDF } from "jspdf";
import { marked } from "marked";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const PAGE_SIZES = {
  "a4": { name: "A4", width: 210, height: 297 },
  "letter": { name: "Letter", width: 215.9, height: 279.4 },
};

function MarkdownToPdf() {
  const [markdown, setMarkdown] = useState(`# Welcome to Markdown to PDF

This is a **bold** text and this is *italic*.

## Features

- Convert markdown to PDF
- Support for headings, lists, and formatting
- Preview your content before converting

### Code Example

\`\`\`javascript
const greeting = "Hello, World!";
console.log(greeting);
\`\`\`

> This is a blockquote

1. First item
2. Second item
3. Third item

---

Made with ❤️ by KayTool`);
  const [title, setTitle] = useState("");
  const [pageSize, setPageSize] = useState("a4");
  const [showPreview, setShowPreview] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Markdown to PDF - Convert MD to PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMarkdown(event.target?.result || "");
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
        toast.success("Markdown file loaded");
      };
      reader.readAsText(file);
    }
  };

  const convertToPdf = () => {
    if (!markdown.trim()) {
      toast.error("Please enter some markdown");
      return;
    }

    try {
      const page = PAGE_SIZES[pageSize];
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [page.width, page.height]
      });

      const margin = 20;
      const maxWidth = page.width - (margin * 2);
      let yPosition = margin;

      // Parse markdown line by line
      const lines = markdown.split('\n');
      
      for (let line of lines) {
        // Check for page overflow
        if (yPosition > page.height - margin) {
          pdf.addPage();
          yPosition = margin;
        }

        // Handle different markdown elements
        if (line.startsWith('# ')) {
          // H1
          pdf.setFontSize(24);
          pdf.setFont(undefined, 'bold');
          const text = line.replace('# ', '');
          pdf.text(text, margin, yPosition);
          yPosition += 12;
        } else if (line.startsWith('## ')) {
          // H2
          pdf.setFontSize(18);
          pdf.setFont(undefined, 'bold');
          const text = line.replace('## ', '');
          pdf.text(text, margin, yPosition);
          yPosition += 10;
        } else if (line.startsWith('### ')) {
          // H3
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          const text = line.replace('### ', '');
          pdf.text(text, margin, yPosition);
          yPosition += 8;
        } else if (line.startsWith('> ')) {
          // Blockquote
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'italic');
          pdf.setTextColor(100, 100, 100);
          const text = line.replace('> ', '');
          const wrapped = pdf.splitTextToSize(text, maxWidth - 10);
          pdf.text(wrapped, margin + 5, yPosition);
          yPosition += wrapped.length * 5 + 2;
          pdf.setTextColor(0, 0, 0);
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          // Unordered list
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'normal');
          const text = '• ' + line.replace(/^[-*] /, '');
          const wrapped = pdf.splitTextToSize(text, maxWidth - 10);
          pdf.text(wrapped, margin + 5, yPosition);
          yPosition += wrapped.length * 5;
        } else if (/^\d+\. /.test(line)) {
          // Ordered list
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'normal');
          const wrapped = pdf.splitTextToSize(line, maxWidth - 10);
          pdf.text(wrapped, margin + 5, yPosition);
          yPosition += wrapped.length * 5;
        } else if (line.startsWith('```')) {
          // Code block marker - skip
          continue;
        } else if (line === '---') {
          // Horizontal rule
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, yPosition, page.width - margin, yPosition);
          yPosition += 5;
        } else if (line.trim() === '') {
          // Empty line
          yPosition += 3;
        } else {
          // Regular paragraph
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'normal');
          // Handle bold and italic
          let processedLine = line
            .replace(/\*\*(.+?)\*\*/g, '$1')
            .replace(/\*(.+?)\*/g, '$1')
            .replace(/`(.+?)`/g, '$1');
          
          const wrapped = pdf.splitTextToSize(processedLine, maxWidth);
          pdf.text(wrapped, margin, yPosition);
          yPosition += wrapped.length * 5;
        }
      }

      const fileName = title ? `${title}.pdf` : `markdown_${Date.now()}.pdf`;
      pdf.save(fileName);
      toast.success("PDF created successfully!");
    } catch (error) {
      console.error("PDF creation error:", error);
      toast.error("Failed to create PDF");
    }
  };

  const getHtmlPreview = () => {
    try {
      return marked(markdown);
    } catch {
      return markdown;
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-violet-50 to-purple-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Code className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-full mb-4">
            <Code className="w-5 h-5" />
            <span className="font-medium">Markdown to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert Markdown to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Write or paste markdown, preview it, then convert to a beautifully formatted PDF.
          </p>
        </div>

        {/* Settings Bar */}
        <Card className="mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Title:</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Document title"
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Page:</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_SIZES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Upload .md:</Label>
                <Input
                  type="file"
                  accept=".md,.markdown,text/markdown"
                  onChange={handleFileUpload}
                  className="w-40 cursor-pointer"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="ml-auto"
              >
                {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {showPreview ? "Hide Preview" : "Show Preview"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Editor and Preview */}
        <div className={`grid ${showPreview ? 'lg:grid-cols-2' : ''} gap-6`}>
          {/* Editor */}
          <Card className="dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                  <Code className="w-4 h-4" /> Markdown
                </h3>
                <span className="text-xs text-slate-500">{markdown.length} chars</span>
              </div>
              <Textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                placeholder="Write your markdown here..."
                className="min-h-[500px] font-mono text-sm resize-none"
              />
            </CardContent>
          </Card>

          {/* Preview */}
          {showPreview && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4" /> Preview
                </h3>
                <div 
                  className="min-h-[500px] p-4 bg-white dark:bg-slate-900 rounded-lg border prose prose-sm dark:prose-invert max-w-none overflow-auto"
                  dangerouslySetInnerHTML={{ __html: getHtmlPreview() }}
                />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="mt-6 text-center">
          <Button 
            onClick={convertToPdf} 
            size="lg"
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:opacity-90"
            disabled={!markdown.trim()}
          >
            <FileText className="w-5 h-5 mr-2" />
            Convert to PDF
          </Button>
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default MarkdownToPdf;
